/**
 * The one symbology the overlay markers and their legend blocks share — spec §F5.5
 * (graduated circles / rings) and §F5.6(c) (the 3-size key + recolour swatches).
 *
 * Same decision as `choroClasses.ts` (NOTES #190), for the same reason: §F5.6(c) prints
 * the **min / mid / max** of the displayed set and a swatch of the colour in force, and
 * both must be the numbers and the colour §F5.5 actually drew with. The mockup solved it by
 * stashing `ov._range` / `ov._col` back onto the state object from inside `renderOverlays`
 * — a render writing to state, which in React means the legend is always one frame behind
 * the map. One hook, two callers, no shared mutable field.
 *
 * §G4's `fillRampLastStop` is read from `useChoroplethClasses()` when an indicator is
 * mapped and from `/meta/config.ramps.green` when none is (the mockup's `rampFamilyColor`
 * falls back to `RAMPS.green` for a null indicator, and §F5.5 does not require a base
 * layer — overlays stand on their own). That is the whole of §F5.11's "restyling fill
 * re-picks overlay colours": the ramp key moves, this value moves, and every consumer of
 * the hook recomputes.
 */
import { useMemo } from 'react';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';

import {
  fetchConfig,
  fetchIndicators,
  fetchUnits,
  fetchValues,
  modeFor,
  queryKeys,
  unitsQueryFor,
  type Indicator,
  type ValuesResponse,
} from '../../lib/api';
import { unitCentroid } from '../../lib/geo';
import {
  adminLevelOf,
  selectScopeParent,
  selectYear,
  useAppStore,
  type ReportingLevel,
} from '../../lib/state';
import { FALLBACK_RAMP_STOPS, overlayColor, symbolRadius } from '../../lib/symbology';
import { useChoroplethClasses } from './choroClasses';

/** One placed marker: where it goes, how big, and what it says. */
export interface OverlayPoint {
  unitId: string;
  unitName: string;
  /** Leaflet order, `[lat, lon]` — §F5.5 `properties.centroid` or the §G7 fallback. */
  at: [number, number];
  value: number;
  /** §F5.5/§G4 `base + √t · span` for the current reporting level. */
  radius: number;
}

export interface OverlayClasses {
  id: string;
  /** 0 = graduated circles, 1 = graduated rings (§F5.5). */
  index: number;
  meta: Indicator | null;
  name: string;
  unitLabel: string;
  fmt: number;
  /** §G4 — the user's override if there is one, else the auto-contrast pick. */
  color: string;
  /** True while `overlays[i].color` is set, so §F5.6(c) can ring the chosen swatch. */
  overridden: boolean;
  /** Min/max of the **displayed** set — the ends of §F5.6(c)'s 3-size key. */
  min: number;
  max: number;
  /** `0|1|2`, or `'watershed'` for the §M3.5 overlay-hierarchy mode. */
  level: ReportingLevel;
  points: OverlayPoint[];
  /** Both queries landed *and* they describe this indicator at this level. */
  ready: boolean;
}

/**
 * §G4's `fillRampLastStop`, whether or not a base indicator is mapped.
 *
 * Callers that already hold a `ChoroClasses` should read `cls.fillRampLastStop` instead;
 * this hook exists for the ones that do not (the §F5.2 sidebar, which colours the `+`
 * button, and this module).
 */
export function useFillRampLastStop(): string {
  const cls = useChoroplethClasses();
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const green = config?.ramps.green;
  return (
    cls?.fillRampLastStop ?? green?.[green.length - 1] ?? FALLBACK_RAMP_STOPS[4]!
  );
}

/**
 * `overlays` resolved into everything §F5.5 and §F5.6(c) need, in overlay order.
 * Empty array when there are no overlays — which is also §F5.6(c)'s auto-hide.
 */
export function useOverlayClasses(): OverlayClasses[] {
  const overlays = useAppStore((s) => s.overlays);
  const level = useAppStore((s) => s.reportingLevel);
  const country = useAppStore((s) => s.country);
  const year = useAppStore(selectYear);
  const cumulative = useAppStore((s) => s.cumulative);
  // §F5.8: the markers are placed on the same units the choropleth paints, so they take the
  // same filter. Without it a scoped map would show three painted communes under a scatter
  // of circles belonging to the whole country.
  const parent = useAppStore(selectScopeParent);
  const fillRampLastStop = useFillRampLastStop();

  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
    enabled: overlays.length > 0,
  });

  // The **same** units key the choropleth uses: an overlay over its own base indicator must
  // not cost a second copy of the geometry, and sharing the key means the markers and the
  // fills can never be drawn from two different generalisations of the same polygons.
  // `unitsQueryFor` is what keeps that true now that a level can be the §M3.5 watershed one.
  const unitsQuery = unitsQueryFor(level);
  const { data: units } = useQuery({
    queryKey: queryKeys.units(unitsQuery.type, unitsQuery.level, country, 'z7', parent),
    queryFn: () => fetchUnits(unitsQuery.type, unitsQuery.level, country, 'z7', parent),
    enabled: overlays.length > 0,
  });

  // `useQueries`, not a `useQuery` per overlay: the array length is state (0, 1 or 2), and
  // a hook cannot be called in a variable-length loop. `keepPreviousData` for the same
  // reason as the choropleth — §F5.11's "year change animates choropleth **and overlays**"
  // is an animation only if the markers stay on screen across the fetch.
  const valueQueries = useQueries({
    queries: overlays.map((o) => {
      // Per overlay, not once for the pane: two overlays can differ in `is_cumulative`,
      // and the flag must be narrowed by the indicator it is being applied to.
      const mode = modeFor(cumulative, indicators?.find((ind) => ind.id === o.id));
      return {
        queryKey: queryKeys.values(o.id, level, year, country, mode, parent),
        queryFn: () => fetchValues(o.id, level, year, country, mode, parent),
        placeholderData: keepPreviousData,
      };
    }),
  });

  // `useQueries` hands back a fresh array — and fresh result objects — on every render, so
  // it cannot itself be a `useMemo` dependency. The payloads inside it *are* stable
  // references (TanStack keeps the same object until the data changes), and there are at
  // most `MAX_OVERLAYS` of them, so they can be spread into fixed dependency slots. Two
  // named consts rather than a loop precisely because the dependency list must be
  // fixed-length; if `MAX_OVERLAYS` ever grows, this grows with it.
  const first = valueQueries[0]?.data;
  const second = valueQueries[1]?.data;
  const payloads = useMemo(() => [first, second], [first, second]);

  return useMemo(
    () =>
      overlays.map((ov, i) => {
        const meta = indicators?.find((ind) => ind.id === ov.id) ?? null;
        // Same guard as `choroClasses`: a held-over payload is safe across a year or a
        // country change but never across a level or an indicator change, where it would
        // size one indicator's circles from another one's numbers.
        const raw: ValuesResponse | undefined = payloads[i];
        const data =
          raw && raw.indicator.id === ov.id && (raw.level === null || raw.level === level)
            ? raw
            : undefined;

        const byUnit = new Map<string, number>();
        for (const row of data?.values ?? []) byUnit.set(row.unit_id, row.value);

        // §G4: "t = (v−min)/(max−min) within the **displayed** set" — the units on screen,
        // not every unit the API answered with.
        const shown: { unitId: string; unitName: string; at: [number, number]; value: number }[] =
          [];
        for (const f of units?.features ?? []) {
          const value = byUnit.get(f.properties.id);
          if (typeof value !== 'number' || !Number.isFinite(value)) continue;
          const at = unitCentroid(f.properties.centroid, f.geometry);
          if (!at) continue;
          shown.push({ unitId: f.properties.id, unitName: f.properties.name, at, value });
        }

        const values = shown.map((s) => s.value);
        const min = values.length ? Math.min(...values) : 0;
        const max = values.length ? Math.max(...values) : 0;

        return {
          id: ov.id,
          index: i,
          meta,
          name: meta?.name ?? ov.id,
          unitLabel: data?.indicator.unit ?? meta?.unit ?? '',
          fmt: data?.indicator.fmt ?? meta?.fmt ?? 1,
          color: overlayColor(i, overlays, fillRampLastStop),
          overridden: Boolean(ov.color),
          min,
          max,
          level,
          points: shown.map((s) => ({
            ...s,
            // The mockup's `mx === mn ? .5 : …`: a displayed set of one, or of identical
            // values, has no spread to encode, and a mid-size symbol says that honestly
            // where a minimum-size one would read as "lowest".
            // §G4 sizes by admin level; a watershed is region-sized, so it borrows level 1.
            radius: symbolRadius(
              max === min ? 0.5 : (s.value - min) / (max - min), adminLevelOf(level),
            ),
          })),
          ready: Boolean(units && data),
        };
      }),
    [overlays, indicators, units, payloads, level, fillRampLastStop],
  );
}
